import React, { FunctionComponent, useEffect, useState } from 'react';
import { cloneDeep, set, get } from 'lodash';

import './Appointment.scss';
import { AppointmentType, appointmentTypeMap } from './AppointmentList';
import { EnabledToggle } from './EnabledToggle';
import { StartsAfterDropdown } from './StartsAfterDropdown';
import { EndsAfterDropdown } from './EndsAfterDropdown';
import { AppointmentWindow } from './AppointmentWindow';
import { TestDriveEnabled } from './TestDriveEnabled';
import { TestDriveRange } from './TestDriveRange';
import { DeliveryRange } from './DeliveryRange';
import { HoursSelection } from './HoursSelection';

interface Props {
  formData: any;
  onChange: Function;
  appointmentType: AppointmentType;
}

export const DAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const defaultDayConfig = {
  enabled: false,
  start: 8,
  end: 18,
};

const defaultConfig = {
  days: {
    [DAYS[0]]: cloneDeep(defaultDayConfig),
    [DAYS[1]]: cloneDeep(defaultDayConfig),
    [DAYS[2]]: cloneDeep(defaultDayConfig),
    [DAYS[3]]: cloneDeep(defaultDayConfig),
    [DAYS[4]]: cloneDeep(defaultDayConfig),
    [DAYS[5]]: cloneDeep(defaultDayConfig),
    [DAYS[6]]: cloneDeep(defaultDayConfig),
  },
};

export const Appointment: FunctionComponent<Props> = ({
  formData,
  onChange,
  appointmentType,
}) => {
  const [scheduleConfig, setScheduleConfig] = useState({} as any);
  const appointmentDisplay = appointmentTypeMap[appointmentType];

  useEffect(() => {
    if (formData) {
      const scheduleConfigCopy = cloneDeep(formData);
      if (!scheduleConfigCopy.days) {
        scheduleConfigCopy.days = cloneDeep(defaultConfig.days);
      }
      setScheduleConfig(scheduleConfigCopy);
    }
  }, [formData]);

  const handleFirstLevelPropChange = (property: string, value: string) => {
    const scheduleConfigCopy = cloneDeep(scheduleConfig);
    set(scheduleConfigCopy, property, value);
    setScheduleConfig(scheduleConfigCopy);
    onChange(scheduleConfigCopy);
  };

  return (
    <div className="appointment-input-group config-group col-md-5">
      <h3 key="label" className="control-label">
        {appointmentDisplay}
      </h3>
      <EnabledToggle
        isEnabled={scheduleConfig.enabled}
        onToggle={handleFirstLevelPropChange}
        property="enabled"
      />
      {scheduleConfig.enabled && (
        <>
          <StartsAfterDropdown
            onchange={handleFirstLevelPropChange}
            property="startsAfter"
            appointmentType={appointmentType}
            value={scheduleConfig.startsAfter}
          />
          <EndsAfterDropdown
            onchange={handleFirstLevelPropChange}
            property="endsAfter"
            appointmentType={appointmentType}
            value={scheduleConfig.endsAfter}
          />
          <div className="input-container">
            <label key="label" className="control-label">
              {appointmentType} Hours
            </label>
            <span className="info">
              Appointments may be scheduled through the last hour.
            </span>
            <div className={`${appointmentType}-hours-section`}>
              {DAYS.map(day => {
                const enabled =
                  get(scheduleConfig, `days.${day}.enabled`) || false;
                return (
                  <HoursSelection
                    key={`${appointmentType}-${day}`}
                    enabled={enabled}
                    day={day}
                    scheduleConfig={scheduleConfig}
                    onChange={onChange}
                    setScheduleConfig={setScheduleConfig}
                    defaultDayConfig={defaultDayConfig}
                    scheduleType={appointmentType}
                  />
                );
              })}
            </div>
          </div>
          {appointmentType === AppointmentType.Delivery && (
            <DeliveryRange
              value={scheduleConfig.radius}
              onChange={handleFirstLevelPropChange}
              property="radius"
            />
          )}
          {(appointmentType === AppointmentType.Pickup ||
            appointmentType === AppointmentType.TestDrive) && (
            <AppointmentWindow
              onChange={handleFirstLevelPropChange}
              property="appointmentWindow"
              appointmentType={appointmentType}
              value={scheduleConfig.appointmentWindow}
            />
          )}
          {appointmentType === AppointmentType.TestDrive && (
            <>
              <TestDriveEnabled
                isEnabled={scheduleConfig.remoteTestDriveEnabled}
                onToggle={handleFirstLevelPropChange}
                property="remoteTestDriveEnabled"
              />
              <TestDriveRange
                value={scheduleConfig.testDriveRadius}
                onChange={handleFirstLevelPropChange}
                property="testDriveRadius"
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Appointment;
